import { z } from "zod";
import { FillSchema } from "./primitives";
import { ElementSchema } from "./elements";
import { TimelineSchema, TransitionSchema } from "./timeline";
import { NotesLayerSchema } from "./layers";
import { MediaRefSchema } from "./media";
import { ConversionWarningSchema } from "./warnings";

export const DECK_SCHEMA_VERSION = 1 as const;

export const LayoutSchema = z.object({
  id: z.string(),
  name: z.string(),
  background: FillSchema.nullable().default(null),
  /** merged master + layout decorations; never animated */
  elements: z.array(ElementSchema).default([]),
});
export type Layout = z.infer<typeof LayoutSchema>;

export const PageSchema = z.object({
  id: z.string(),
  index: z.number().int().min(0),
  layoutId: z.string().nullable().default(null),
  /** slide-level override; null => use the layout background */
  background: FillSchema.nullable().default(null),
  /** z-order = array order */
  elements: z.array(ElementSchema).default([]),
  timeline: TimelineSchema.default({}),
  transition: TransitionSchema.nullable().default(null),
  notes: NotesLayerSchema.default({}),
  hidden: z.boolean().default(false),
});
export type Page = z.infer<typeof PageSchema>;

export const FontUsageSchema = z.object({
  requested: z.string(),
  resolved: z.string(),
  source: z.enum(["theme", "explicit"]),
  status: z.enum(["exact", "metricCompatible", "substituted"]),
  /** How the player should load it: a Google Fonts family, a self-hosted file under /fonts, or nothing. */
  load: z.enum(["google", "self", "none"]).default("none"),
});
export type FontUsage = z.infer<typeof FontUsageSchema>;

export const DeckSchema = z.object({
  schemaVersion: z.literal(DECK_SCHEMA_VERSION),
  id: z.string(),
  title: z.string(),
  /** pt */
  size: z.object({ w: z.number().positive(), h: z.number().positive() }),
  layouts: z.record(LayoutSchema).default({}),
  media: z.record(MediaRefSchema).default({}),
  fonts: z.array(FontUsageSchema).default([]),
  pages: z.array(PageSchema).min(1),
  warnings: z.array(ConversionWarningSchema).default([]),
  source: z
    .object({
      kind: z.enum(["pptx", "pdf", "images"]),
      filename: z.string(),
      convertedAt: z.string(),
      converterVersion: z.string(),
    })
    .optional(),
});
export type Deck = z.infer<typeof DeckSchema>;

export function validateDeck(input: unknown): Deck {
  return DeckSchema.parse(input);
}
